// Lazy require to break the sales↔hospitality require cycle.
// sales/public-api → (useSaleDetail) → hospitality/public-api → kitchenTicketPresentation → sales/public-api.
const formatSaleShortRefLabel = (saleId: string | null | undefined): string =>
  require('../../sales/public-api').formatSaleShortRefLabel(saleId);
import type {
  KitchenTicket,
  KitchenTicketLine,
  KitchenTicketStatus,
  ProductStation,
  Station,
} from './hospitalityTypes';

export type KitchenModifierKind = 'extra' | 'exclude' | 'allergen' | 'detail';

export type KitchenModifier = {
  label: string;
  quantity: number;
  kind: KitchenModifierKind;
};

function modifierKind(label: string, priceDelta = 0): KitchenModifierKind {
  const normalized = label.trim().toLowerCase();
  if (/^(sin|no)\b/.test(normalized)) return 'exclude';
  if (priceDelta > 0 || /^(extra|adicional)\b/.test(normalized)) return 'extra';
  return 'detail';
}

/** Kind explícito del choice gana; null/legacy cae a la heurística de texto. */
function resolveModifierKind(
  explicit: 'extra' | 'exclude' | 'detail' | null | undefined,
  label: string,
  priceDelta = 0,
): KitchenModifierKind {
  return explicit ?? modifierKind(label, priceDelta);
}

/** Hide option subtitles already contained in the product name (never mutate the name). */
export function displayKitchenLine(line: KitchenTicketLine): {
  name: string;
  optionsLabel: string | null;
  modifiers: KitchenModifier[];
  lineNote: string | null;
  productId: string | null;
  status: 'pending' | 'done';
  comboName: string | null;
} {
  const name = (line.name ?? '').trim() || 'Ítem';
  const nameLc = name.toLowerCase();
  const structuredOptions = Array.isArray(line.menuOptions) ? line.menuOptions : [];
  const rawOptions: KitchenModifier[] = structuredOptions.length
    ? structuredOptions.flatMap((option) => {
        const label = (option.kitchenLabel ?? option.choiceName).trim();
        const mods: KitchenModifier[] = [
          {
            label,
            quantity: Math.max(1, Number(option.qty) || 1),
            kind: resolveModifierKind(
              option.modifierKind,
              option.choiceName,
              Number(option.priceDelta) || 0,
            ),
          },
        ];
        // Alérgenos del choice: chip propio, no depende del nombre.
        for (const tag of option.allergenTags ?? []) {
          const tagLabel = String(tag).trim();
          if (tagLabel) {
            mods.push({ label: `Contiene ${tagLabel}`, quantity: 1, kind: 'allergen' });
          }
        }
        return mods;
      })
    : (line.optionsLabel ?? '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .map((label) => ({
          label: label.replace(/×\d+$/i, '').trim(),
          quantity: Math.max(1, Number(label.match(/×(\d+)$/i)?.[1]) || 1),
          kind: modifierKind(label),
        }));
  const modifiers = rawOptions.filter((option) => {
    const token = option.label.toLowerCase();
    return token && !nameLc.includes(token);
  });
  const fallbackOptionsLabel = (line.optionsLabel ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const token = part.replace(/×\d+$/i, '').trim().toLowerCase();
      return token && !nameLc.includes(token);
    })
    .join(', ');
  return {
    name,
    optionsLabel: structuredOptions.length
      ? modifiers.length ? modifiers.map((option) => option.label).join(', ') : null
      : fallbackOptionsLabel || null,
    modifiers,
    lineNote: line.lineNote?.trim() || null,
    productId: line.productId ?? null,
    status: line.status ?? 'pending',
    comboName: line.comboName?.trim() || null,
  };
}

export type KitchenTicketSource = 'pos' | 'online' | 'other';

export type KitchenAllDayCount = {
  key: string;
  name: string;
  optionsLabel: string | null;
  quantity: number;
};

export type KitchenLineDisplayBlock =
  | { kind: 'single'; index: number; raw: KitchenTicketLine }
  | {
      kind: 'combo';
      comboName: string;
      comboQty: number;
      items: Array<{ index: number; raw: KitchenTicketLine }>;
    };

/** Agrupa líneas consecutivas del mismo combo para el KDS. */
export function groupKitchenTicketLines(lines: KitchenTicketLine[]): KitchenLineDisplayBlock[] {
  const blocks: KitchenLineDisplayBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i]!;
    const comboName = raw.comboName?.trim();
    if (!comboName) {
      blocks.push({ kind: 'single', index: i, raw });
      i += 1;
      continue;
    }
    const items: Array<{ index: number; raw: KitchenTicketLine }> = [{ index: i, raw }];
    i += 1;
    while (i < lines.length && lines[i]?.comboName?.trim() === comboName) {
      items.push({ index: i, raw: lines[i]! });
      i += 1;
    }
    const comboQty = Math.max(1, Number(items[0]?.raw.qty) || 1);
    blocks.push({ kind: 'combo', comboName, comboQty, items });
  }
  return blocks;
}

/** Counts active all-day lines for the kitchen production view. */
export function summarizeAllDayLines(tickets: KitchenTicket[]): KitchenAllDayCount[] {
  const counts = new Map<string, KitchenAllDayCount>();
  for (const ticket of tickets) {
    if (!ticket.isAllDay || ticket.status === 'served' || ticket.status === 'cancelled') continue;
    for (const rawLine of ticket.lines ?? []) {
      if (rawLine.status === 'done') continue;
      const line = displayKitchenLine(rawLine);
      const key = `${line.name}::${line.optionsLabel ?? ''}`;
      const current = counts.get(key);
      if (current) {
        current.quantity += Math.max(1, Number(rawLine.qty) || 1);
      } else {
        counts.set(key, {
          key,
          name: line.name,
          optionsLabel: line.optionsLabel,
          quantity: Math.max(1, Number(rawLine.qty) || 1),
        });
      }
    }
  }
  return Array.from(counts.values()).sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export type KitchenProductionTicketRef = {
  ticketId: string;
  ref: string;
  quantity: number;
};

export type KitchenProductionItemSummary = {
  key: string;
  name: string;
  optionsLabel: string | null;
  quantity: number;
  ticketRefs: KitchenProductionTicketRef[];
};

export type KitchenProductionStationGroup = {
  stationId: string | null;
  stationName: string;
  totalUnits: number;
  items: KitchenProductionItemSummary[];
};

/** Calcula el avance de líneas (hechas vs totales) de una comanda. */
export function kitchenTicketLineProgress(
  lines: KitchenTicketLine[] | undefined | null,
): { done: number; total: number } {
  const list = lines ?? [];
  let done = 0;
  for (const l of list) {
    if (l.status === 'done') done += 1;
  }
  return { done, total: list.length };
}

/**
 * Agrupa las unidades pendientes de preparación (tickets en pending/preparing y líneas no hechas)
 * por estación cuando la sucursal tiene estaciones configuradas, o en un único grupo general
 * cuando no hay estaciones configuradas.
 */
export function summarizeKitchenProductionGroups(
  tickets: KitchenTicket[],
  stations: Station[] = [],
  productStations: ProductStation[] = [],
): KitchenProductionStationGroup[] {
  const activeTickets = tickets.filter(
    (t) => t.status === 'pending' || t.status === 'preparing',
  );

  const stationIdsByProduct = new Map<string, string[]>();
  const validStationIds = new Set(stations.map((s) => s.id));
  for (const ps of productStations) {
    if (!validStationIds.has(ps.stationId)) continue;
    const list = stationIdsByProduct.get(ps.productId) ?? [];
    if (!list.includes(ps.stationId)) list.push(ps.stationId);
    stationIdsByProduct.set(ps.productId, list);
  }

  const addLineToBucket = (
    bucket: Map<string, KitchenProductionItemSummary>,
    ticket: KitchenTicket,
    rawLine: KitchenTicketLine,
  ) => {
    const line = displayKitchenLine(rawLine);
    const qty = Math.max(1, Number(rawLine.qty) || 1);
    const key = `${line.name}::${line.optionsLabel ?? ''}`;
    const ref = kitchenTicketPrimaryRef(ticket);
    const existing = bucket.get(key);
    if (existing) {
      existing.quantity += qty;
      const existingRef = existing.ticketRefs.find((r) => r.ticketId === ticket.id);
      if (existingRef) {
        existingRef.quantity += qty;
      } else {
        existing.ticketRefs.push({ ticketId: ticket.id, ref, quantity: qty });
      }
    } else {
      bucket.set(key, {
        key,
        name: line.name,
        optionsLabel: line.optionsLabel,
        quantity: qty,
        ticketRefs: [{ ticketId: ticket.id, ref, quantity: qty }],
      });
    }
  };

  const sortBucket = (bucket: Map<string, KitchenProductionItemSummary>): KitchenProductionItemSummary[] =>
    Array.from(bucket.values()).sort((a, b) => {
      if (b.quantity !== a.quantity) return b.quantity - a.quantity;
      return a.name.localeCompare(b.name, 'es');
    });

  if (stations.length === 0) {
    const singleBucket = new Map<string, KitchenProductionItemSummary>();
    for (const ticket of activeTickets) {
      for (const rawLine of ticket.lines ?? []) {
        if (rawLine.status === 'done') continue;
        addLineToBucket(singleBucket, ticket, rawLine);
      }
    }
    const items = sortBucket(singleBucket);
    const totalUnits = items.reduce((sum, it) => sum + it.quantity, 0);
    return [
      {
        stationId: null,
        stationName: 'Total a preparar',
        totalUnits,
        items,
      },
    ];
  }

  const sortedStations = [...stations].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.name.localeCompare(b.name, 'es');
  });

  const bucketsByStation = new Map<string, Map<string, KitchenProductionItemSummary>>();
  for (const st of sortedStations) {
    bucketsByStation.set(st.id, new Map());
  }
  const unassignedBucket = new Map<string, KitchenProductionItemSummary>();

  for (const ticket of activeTickets) {
    for (const rawLine of ticket.lines ?? []) {
      if (rawLine.status === 'done') continue;
      const assignedIds = rawLine.productId ? stationIdsByProduct.get(rawLine.productId) : undefined;
      if (assignedIds && assignedIds.length > 0) {
        for (const stId of assignedIds) {
          const bucket = bucketsByStation.get(stId);
          if (bucket) addLineToBucket(bucket, ticket, rawLine);
        }
      } else {
        addLineToBucket(unassignedBucket, ticket, rawLine);
      }
    }
  }

  const groups: KitchenProductionStationGroup[] = sortedStations.map((st) => {
    const items = sortBucket(bucketsByStation.get(st.id) ?? new Map());
    const totalUnits = items.reduce((sum, it) => sum + it.quantity, 0);
    return {
      stationId: st.id,
      stationName: st.name,
      totalUnits,
      items,
    };
  });

  if (unassignedBucket.size > 0) {
    const items = sortBucket(unassignedBucket);
    const totalUnits = items.reduce((sum, it) => sum + it.quantity, 0);
    groups.push({
      stationId: null,
      stationName: 'Cocina general',
      totalUnits,
      items,
    });
  }

  return groups;
}

export type KitchenPrintJobPlan = {
  /** null → impresora por defecto de la sucursal (`kitchenPrinterOverride`). */
  stationId: string | null;
  lines: KitchenTicketLine[];
};

/**
 * Plan de auto-impresión por estación: si alguna estación tiene impresora
 * asignada, la comanda se splitea — cada estación con impresora recibe un job
 * con solo sus líneas; el resto (productos sin asignar o de estaciones sin
 * impresora) va en un único job a la impresora por defecto de la sucursal.
 * Si ninguna estación tiene impresora, devuelve un solo job con la comanda
 * entera (comportamiento histórico). Un producto asignado a varias estaciones
 * con impresora imprime en cada una.
 */
export function planKitchenPrintJobs(
  ticket: KitchenTicket,
  stations: Station[] = [],
  productStations: ProductStation[] = [],
  stationPrintersMap: Record<string, string> = {},
): KitchenPrintJobPlan[] {
  const lines = ticket.lines ?? [];
  const stationsWithPrinter = stations.filter(
    (s) => (stationPrintersMap[s.id] ?? '').trim().length > 0,
  );
  if (stationsWithPrinter.length === 0 || lines.length === 0) {
    return [{ stationId: null, lines }];
  }

  const productIdsByStation = new Map<string, Set<string>>();
  for (const ps of productStations) {
    if (!productIdsByStation.has(ps.stationId)) {
      productIdsByStation.set(ps.stationId, new Set<string>());
    }
    productIdsByStation.get(ps.stationId)!.add(ps.productId);
  }

  const jobs: KitchenPrintJobPlan[] = [];
  const coveredLineIdx = new Set<number>();
  for (const station of stationsWithPrinter) {
    const productIds = productIdsByStation.get(station.id);
    if (!productIds || productIds.size === 0) continue;
    const stationLines = lines.filter((line, idx) => {
      if (line.productId && productIds.has(line.productId)) {
        coveredLineIdx.add(idx);
        return true;
      }
      return false;
    });
    if (stationLines.length > 0) {
      jobs.push({ stationId: station.id, lines: stationLines });
    }
  }

  const rest = lines.filter((_, idx) => !coveredLineIdx.has(idx));
  if (rest.length > 0) {
    jobs.push({ stationId: null, lines: rest });
  }
  return jobs.length > 0 ? jobs : [{ stationId: null, lines }];
}

const ONLINE_ORDER_REF_RE = /pedido\s*#\s*([a-z0-9]+)/i;
const POS_COUNTER_REF_RE = /pedido\s*#\s*(\d+)/i;
const POS_SALE_REF_RE = /(?:pos\s*[·.•-]?\s*)?venta\s*#?\s*([a-f0-9]{6,12})/i;
const CO_SENTINEL_RE = /^co:[0-9a-f-]{36}$/i;
/** Línea que solo repite la ref (ya va en el título). */
const REF_ONLY_LINE_RE = /^(?:pedido|venta)\s*#\s*[a-z0-9]+$/i;

export function kitchenTicketSource(ticket: KitchenTicket): KitchenTicketSource {
  if (ticket.customerOrderId) return 'online';
  const notes = ticket.notes ?? '';
  const notesLc = notes.toLowerCase();

  // POS mostrador: Pedido #N (contador numérico) o venta ligada — antes que online genérico.
  if (ticket.saleId || POS_COUNTER_REF_RE.test(notes) || POS_SALE_REF_RE.test(notes)) {
    return 'pos';
  }
  if (notesLc.includes('venta #')) return 'pos';

  const firstLine = notes.trim().split('\n')[0] ?? '';
  if (CO_SENTINEL_RE.test(firstLine) || firstLine.toLowerCase().startsWith('co:')) {
    return 'online';
  }
  if (notesLc.includes('pedido #')) return 'online';
  if (notesLc.includes('pos')) return 'pos';
  return 'other';
}

/** Badge Para llevar / Comer acá / Delivery en tarjeta KDS. */
export function kitchenFulfillmentBadgeLabel(
  fulfillmentType: string | null | undefined,
): string | null {
  switch (fulfillmentType) {
    case 'pickup':
      return 'Para llevar';
    case 'dine_in':
      return 'Comer acá';
    case 'delivery':
      return 'Delivery';
    default:
      return null;
  }
}

/** Primary CTA labels; online uses Archivar comanda instead of Servido (handoff lives in Pedidos). */
export function kitchenActionLabel(
  nextStatus: KitchenTicketStatus,
  source: KitchenTicketSource,
  fulfillmentType?: string | null,
): string {
  if (nextStatus === 'preparing') return 'Empezar';
  if (nextStatus === 'ready') {
    if (fulfillmentType === 'pickup') return 'Listo para retirar';
    if (fulfillmentType === 'delivery') return 'Listo para enviar';
    return 'Listo';
  }
  if (nextStatus === 'served') {
    if (fulfillmentType === 'pickup') return 'Confirmar retiro';
    if (fulfillmentType === 'delivery') return 'Confirmar entrega';
    if (source === 'online') return 'Archivar comanda';
    return 'Servido';
  }
  return nextStatus;
}

/**
 * Referencia primaria para KDS / comanda impresa.
 * Online → #PV3G; POS → #a1b2c3 (misma regla que Ventas); nunca el UUID del ticket.
 * Refs numéricos cortos (contador por fuente) llevan sufijo de origen —
 * dos fuentes pueden compartir #4 (venta POS y pedido online/mesa) y el
 * sufijo las desambigua: #4·POS / #4·WEB / #4·MESA.
 */
export function kitchenTicketPrimaryRef(ticket: KitchenTicket): string {
  const base = kitchenTicketPrimaryRefBase(ticket);
  if (!/^#\d+$/.test(base)) return base;
  if (ticket.customerOrderId) return `${base}·WEB`;
  if (ticket.saleId) return `${base}·POS`;
  if (ticket.diningTableId) return `${base}·MESA`;
  return base;
}

function kitchenTicketPrimaryRefBase(ticket: KitchenTicket): string {
  const notes = ticket.notes ?? '';
  const counterMatch = notes.match(POS_COUNTER_REF_RE);
  if (counterMatch?.[1]) return `#${counterMatch[1]}`;

  const orderMatch = notes.match(ONLINE_ORDER_REF_RE);
  if (orderMatch?.[1]) return `#${orderMatch[1].toUpperCase()}`;

  if (ticket.saleId) {
    const saleLabel = formatSaleShortRefLabel(ticket.saleId);
    if (saleLabel !== '—') return saleLabel;
  }

  const posMatch = notes.match(POS_SALE_REF_RE);
  if (posMatch?.[1]) {
    const token = posMatch[1].replace(/-/g, '').toLowerCase();
    // Preferir sufijo canónico de 6 si el token es más largo (legacy prefijo UUID).
    const suffix = token.length > 6 ? token.slice(-6) : token;
    return `#${suffix}`;
  }

  const compact = (ticket.id ?? '').replace(/-/g, '');
  return compact ? `#${compact.slice(0, 8)}` : '—';
}

/** Notas visibles: sin sentinel co: ni línea que solo repite Pedido/Venta #ref. */
export function kitchenTicketPublicNotes(notes: string | null | undefined): string {
  if (!notes) return '';
  return notes
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !CO_SENTINEL_RE.test(line) && !REF_ONLY_LINE_RE.test(line))
    .map((line) =>
      line
        .replace(/^pedido\s*#\s*[a-z0-9]+\s*[·.•-]\s*/i, '')
        .replace(/^(?:pos\s*[·.•-]?\s*)?venta\s*#?\s*[a-f0-9]{6,12}\s*[·.•-]?\s*/i, '')
        .trim(),
    )
    .filter(Boolean)
    .join('\n')
    .trim();
}

export function kitchenTicketSourceLabel(source: KitchenTicketSource): string {
  if (source === 'online') return 'Online';
  if (source === 'pos') return 'POS';
  return 'Cocina';
}

export function kitchenWaitLabel(createdAt: string | null | undefined, nowMs = Date.now()): string {
  if (!createdAt) return '';
  const ms = Date.parse(createdAt);
  if (!Number.isFinite(ms)) return '';
  const mins = Math.max(0, Math.floor((nowMs - ms) / 60_000));
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

const STATUS_PHASE_LABEL: Partial<Record<KitchenTicketStatus, string>> = {
  pending: 'Nueva',
  preparing: 'En prep',
  ready: 'En listo',
};

/** Minutes in the current open status (falls back to ticket age). */
export function kitchenStatusPhaseLabel(
  status: KitchenTicketStatus,
  statusEnteredAt: string | null | undefined,
  createdAt: string | null | undefined,
  nowMs = Date.now(),
): string {
  if (status === 'served' || status === 'cancelled') {
    const wait = kitchenWaitLabel(createdAt, nowMs);
    return wait ? `Hace ${wait}` : '';
  }
  const anchor = statusEnteredAt ?? createdAt;
  if (!anchor) return STATUS_PHASE_LABEL[status] ?? '';
  const ms = Date.parse(anchor);
  if (!Number.isFinite(ms)) return STATUS_PHASE_LABEL[status] ?? '';
  const mins = Math.max(0, Math.floor((nowMs - ms) / 60_000));
  const phase = STATUS_PHASE_LABEL[status];
  const time =
    mins < 1 ? '<1 min' : mins < 60 ? `${mins} min` : kitchenWaitLabel(anchor, nowMs);
  return phase ? `${phase} · ${time}` : time;
}

/** SLA countdown for preparing tickets; null when no deadline. */
export function kitchenSlaCountdownLabel(
  slaDeadlineAt: string | null | undefined,
  nowMs = Date.now(),
): string | null {
  if (!slaDeadlineAt) return null;
  const deadlineMs = Date.parse(slaDeadlineAt);
  if (!Number.isFinite(deadlineMs)) return null;
  const diffMs = deadlineMs - nowMs;
  const mins = Math.max(1, Math.ceil(Math.abs(diffMs) / 60_000));
  if (diffMs >= 0) return mins === 1 ? 'Queda 1 min' : `Quedan ${mins} min`;
  return mins === 1 ? 'Atrasada 1 min' : `Atrasada ${mins} min`;
}

/** Labels unresolved tickets from a previous local calendar day. */
export function kitchenTicketDayLabel(
  createdAt: string | null | undefined,
  nowMs = Date.now(),
): string | null {
  if (!createdAt) return null;
  const createdMs = Date.parse(createdAt);
  if (!Number.isFinite(createdMs)) return null;
  const startOfDay = (ms: number) => {
    const date = new Date(ms);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  };
  const dayDiff = Math.floor((startOfDay(nowMs) - startOfDay(createdMs)) / 86_400_000);
  if (dayDiff <= 0) return null;
  if (dayDiff === 1) return 'Ayer';
  const date = new Date(createdMs);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

export type TicketAgeTone = 'fresh' | 'warn' | 'danger';

export function ticketAgeTone(
  createdAt: string | null | undefined,
  nowMs = Date.now(),
  warnMinutes = 5,
  dangerMinutes = 10,
  statusEnteredAt?: string | null,
  slaDeadlineAt?: string | null,
): TicketAgeTone {
  if (slaDeadlineAt) {
    const deadlineMs = Date.parse(slaDeadlineAt);
    if (Number.isFinite(deadlineMs)) {
      if (nowMs > deadlineMs) return 'danger';
      const minsToDeadline = (deadlineMs - nowMs) / 60_000;
      if (minsToDeadline <= Math.max(1, warnMinutes / 2)) return 'warn';
    }
  }
  const anchor = statusEnteredAt ?? createdAt ?? '';
  const ms = Date.parse(anchor);
  if (!Number.isFinite(ms)) return 'fresh';
  const mins = Math.floor((nowMs - ms) / 60_000);
  if (mins >= Math.max(warnMinutes, dangerMinutes)) return 'danger';
  if (mins >= Math.max(0, warnMinutes)) return 'warn';
  return 'fresh';
}

/**
 * KDS filters (Toast/Square-style):
 * - queue = prep station (new + cooking)
 * - ready = expo / waiting handoff
 * - recent = served/cancelled in the snapshot window (recall board)
 * - pending | preparing = drill-downs of queue
 * - unpaid = fire-before-pay tickets with payment_state=pending (any open status)
 */
export type KitchenStatusFilter = 'queue' | 'pending' | 'preparing' | 'ready' | 'recent' | 'unpaid';
export type KitchenScreenRole = 'prep' | 'expo';

/** Expo works on handoff-ready tickets; prep keeps the full KDS queue available. */
export function filterKitchenTicketsForRole(
  tickets: KitchenTicket[],
  role: KitchenScreenRole,
): KitchenTicket[] {
  if (role === 'prep') return tickets;
  return tickets.filter((ticket) =>
    ticket.status === 'ready' || ticket.status === 'served' || ticket.status === 'cancelled',
  );
}

export function filterKitchenTickets(
  tickets: KitchenTicket[],
  filter: KitchenStatusFilter,
): KitchenTicket[] {
  if (filter === 'queue') {
    return tickets.filter((t) => t.status === 'pending' || t.status === 'preparing');
  }
  if (filter === 'recent') {
    return tickets.filter((t) => t.status === 'served' || t.status === 'cancelled');
  }
  if (filter === 'unpaid') {
    return tickets.filter((t) => t.paymentState === 'pending' && t.status !== 'cancelled');
  }
  return tickets.filter((t) => t.status === filter);
}

/** Toast-style recall target: archived → ready; cancelled → preparing. */
export function kitchenRecallTargetStatus(
  status: KitchenTicketStatus,
): KitchenTicketStatus | null {
  if (status === 'served') return 'ready';
  if (status === 'cancelled') return 'preparing';
  return null;
}

export function isKitchenRecallable(ticket: KitchenTicket): boolean {
  return kitchenRecallTargetStatus(ticket.status) != null;
}

/** KDS-S1: open tickets for a table, ready for Mesas to consume. */
export function listOpenTicketsForTable(
  tableId: string,
  tickets: KitchenTicket[],
): { tickets: KitchenTicket[]; count: number; worstStatus: KitchenTicketStatus | null } {
  const open = tickets.filter(
    (t) => t.diningTableId === tableId && t.status !== 'served' && t.status !== 'cancelled',
  );
  const worst = open.reduce<KitchenTicketStatus | null>((acc, t) => {
    if (!acc) return t.status;
    const order: KitchenTicketStatus[] = ['pending', 'preparing', 'ready', 'served', 'cancelled'];
    return order.indexOf(t.status) > order.indexOf(acc) ? t.status : acc;
  }, null);
  return { tickets: open, count: open.length, worstStatus: worst };
}
